import { useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { MenuPartner } from "@/assets/icons/icons";
import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { Modal } from "@/components/Interface/Modal/Modal";
import { BrandLoadingScreen } from "@/components/Layout/BrandLoadingScreen/BrandLoadingScreen";
import { ButtonGroupFlexBox } from "@/components/Layout/ButtonGroupFlexBox/ButtonGroupFlexBox";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import {
  BusinessPersonalDetailsFormFields,
  excludedFieldsBusiness,
  excludedFieldsPrivate,
  fk,
  PersonalDetailsFormFields,
} from "@/components/Portal/UserAccount/PersonalDetails/PersonalDetailsFormFields/PersonalDetailsFormFields";
import { SCHWEIZ } from "@/constants/countries";
import { useAlert } from "@/hooks/useAlert";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  getGetCustomerQueryKey,
  useGetCustomer,
  useGetCustomerTribe,
  useLeaveTribe,
  useUpdateCustomer,
} from "@/services/api";
import { BasicCustomerData } from "@/services/model";
import { AlertTypes } from "@/utils/atoms";
import {
  isBusinessCustomer,
  PLACEHOLDER_ADDRESS_TEXT,
  PLACEHOLDER_ADDRESS_ZIP,
  transformBasicCustomerDataToPersonalDetailsForm,
  transformPersonalDetailsFormToBasicCustomerData,
} from "@/utils/customerUtils";
import { log } from "@/utils/log";

export const PersonalDataOnboarding: FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const baseLanguage = i18n.language.split("-")[0];

  const { data: customerData, isLoading } = useGetCustomer();
  const { data: customerTribe, refetch: refetchCustomerTribe } =
    useGetCustomerTribe();
  const { mutate: leaveTribe } = useLeaveTribe();
  const { mutateAsync: updateCustomer, isLoading: isUpdating } =
    useUpdateCustomer();
  const showAlert = useAlert();
  const [showBusinessModal, setShowBusinessModal] = useState(false);

  const isBusiness = customerData ? isBusinessCustomer(customerData) : false;

  const methods = useForm<BusinessPersonalDetailsFormFields>({
    mode: "onChange",
    defaultValues: {
      formOfAddress: "0",
      firstName: "",
      lastName: "",
      street: "",
      houseNumber: "",
      zip: "",
      city: "",
      country: SCHWEIZ,
      phone: "+41",
      dateOfBirth: "",
      organizationName: "",
      organizationForm: { id: 0, description: "" },
      organizationId: "",
    },
  });

  useEffect(() => {
    if (customerData) {
      const prefillData =
        transformBasicCustomerDataToPersonalDetailsForm(customerData);
      Object.entries(prefillData).forEach(([key, value]) => {
        if (key === "firstName" && !customerData.firstName) {
          return;
        }
        if (key === "lastName" && !customerData.lastName) {
          return;
        }
        // Date of birth is never prefilled — customers re-enter it explicitly
        // rather than silently keeping whatever placeholder is on file.
        if (key === "dateOfBirth") {
          return;
        }
        // Street/city/zip are seeded with placeholder values at registration
        // (see RegisterForm.tsx) before the customer has a real address on file.
        if (
          (key === "street" || key === "city") &&
          value === PLACEHOLDER_ADDRESS_TEXT
        ) {
          return;
        }
        if (key === "zip" && value === PLACEHOLDER_ADDRESS_ZIP) {
          return;
        }
        methods.setValue(
          key as keyof BusinessPersonalDetailsFormFields,
          value,
          { shouldDirty: false, shouldTouch: false },
        );
      });
    }
  }, [customerData, methods]);

  const onSubmit = async (data: BusinessPersonalDetailsFormFields) => {
    if (!customerData) {
      return;
    }

    const postBody = transformPersonalDetailsFormToBasicCustomerData(
      data,
      customerData,
      baseLanguage,
      t,
    );

    try {
      const updatedCustomer = await updateCustomer({ data: postBody });

      // Portal gates cockpit/FWB on hasCompleteMandatoryPersonalData using the
      // cached GET /customer result. Without updating that cache here, navigate
      // would bounce straight back to this page with a stale incomplete customer
      // — and dateOfBirth is never prefilled on remount, so the next submit
      // fails validation for a field the user already entered.
      queryClient.setQueryData(
        getGetCustomerQueryKey(),
        (previousCustomer: BasicCustomerData | undefined) => ({
          ...(previousCustomer ?? customerData),
          ...postBody,
          ...(updatedCustomer ?? {}),
        }),
      );

      navigate(
        customerTribe?.tribeId
          ? portalRoutes.friendsWithBenefits.groupOverview
          : portalRoutes.cockpit.base,
        { replace: true },
      );
    } catch (error) {
      if (
        `${(error as any)?.response?.data}`.includes("invalid phone number")
      ) {
        methods.setError("phone", {
          type: "backendError",
          message: t("label.validation.phoneBackend"),
        });
      } else {
        log("Error saving personal data during onboarding: " + error);
        showAlert({
          type: AlertTypes.error,
          text: t("Common.generalForm.error.genericError"),
        });
      }
    }
  };

  if (isLoading) {
    return <BrandLoadingScreen />;
  }

  return (
    <>
      {showBusinessModal && (
        <Modal
          className="max-w-[628px]"
          hasScroll={false}
          title={
            <h2 className="flex gap-4">
              <MenuPartner
                className="flex h-12 w-12 items-end justify-end"
                fill="#0014FF"
              />
              {t(
                "Onboarding.sections.personal-details.friendsWithBenefitsPopUp.title",
              )}
            </h2>
          }
          onClose={() => setShowBusinessModal(false)}
        >
          <div>
            <div className="flex flex-col gap-4 pl-16">
              <p>
                <Trans
                  t={t}
                  i18nKey="Onboarding.sections.personal-details.friendsWithBenefitsPopUp.text1"
                />
              </p>
              <p className="font-semibold">
                <Trans
                  t={t}
                  i18nKey="Onboarding.sections.personal-details.friendsWithBenefitsPopUp.text2"
                />
              </p>
              <p className="text-sm text-primary-100">
                <Trans
                  t={t}
                  i18nKey="Onboarding.sections.personal-details.friendsWithBenefitsPopUp.text3"
                />
              </p>
            </div>
            <ButtonGroupFlexBox
              primaryButton={
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => {
                    leaveTribe(undefined, {
                      onSuccess: () => {
                        refetchCustomerTribe();
                      },
                      onSettled: () => {
                        setShowBusinessModal(false);
                      },
                    });
                  }}
                >
                  {t(
                    "Onboarding.sections.personal-details.friendsWithBenefitsPopUp.cta2",
                  )}
                </Button>
              }
              secondaryButton={
                <Button
                  variant="secondary"
                  type="button"
                  onClick={() => setShowBusinessModal(false)}
                >
                  {t(
                    "Onboarding.sections.personal-details.friendsWithBenefitsPopUp.cta1",
                  )}
                </Button>
              }
            />
          </div>
        </Modal>
      )}

      <h1 className="px-4 pb-6 pt-4 text-center text-headline-xl font-semibold leading-xs tracking-tight text-foreground-primary desktop:px-8">
        {t("portal:personalDataOnboarding.headline")}
      </h1>
      <div className="mx-auto max-w-2xl px-4 pb-8">
        <h2 className="mb-8 text-headline-md font-semibold text-foreground-primary">
          {t("Onboarding.sections.personal-details.header")}
        </h2>

        {isUpdating && <LoadingSpinner />}

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <PersonalDetailsFormFields
              excludedFields={
                isBusiness ? excludedFieldsBusiness : excludedFieldsPrivate
              }
              // Business: lock company identity fields
              readonlyFields={
                isBusiness
                  ? [
                      fk.organizationName,
                      fk.organizationForm,
                      fk.organizationId,
                    ]
                  : []
              }
              disableAllFields={isUpdating}
              onBusinessSectionToggle={(checked) => {
                if (checked && customerTribe?.tribeId) {
                  setShowBusinessModal(true);

                  return false;
                }
              }}
            />

            <div className="mt-6 flex justify-center tablet:mt-10">
              <SubmitButton
                className="accent capitalize"
                label={t("Common.label.complete")}
                disabled={isUpdating}
                fullWidth={false}
              />
            </div>
          </form>
        </FormProvider>
      </div>
    </>
  );
};
